import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { GameplayBackdrop } from '../ui/GameplayBackdrop';
import { PlayerAvatar } from '../ui/PlayerAvatar';
import { CountryFlag } from '../ui/CountryFlag';
import { apiClient } from '../../network/ApiClient';
import type { RoundEndPayload } from '../../types/roundEnd';
import type { Player } from '../../types/game';
import { useTranslation } from '../../i18n/react';
import { t as translate } from '../../i18n';
import { getPlayerName } from '../../utils/playerName';

/**
 * MatchEndOverlay — the redesigned FINAL Match End (Victory/Defeat) screen.
 *
 * Renders inside the canonical GameplayBackdrop (full-viewport blur/tint,
 * board mounted behind, inert background, focus trap, non-dismissible) for
 * sessionOver payloads that carry a rematch snapshot — i.e. every naturally
 * completed match on the current backend. Legacy sessionOver terminals
 * WITHOUT a snapshot keep rendering the existing RoundEndOverlay fallback
 * (GameScreen routing) — this component never invents rematch functionality
 * for them.
 *
 * The authoritative 20s rematch flow is consumed exactly as-is: votes go
 * through useRoundEndOverlay.submitRematchVote (single-flight, funneled to
 * GameScene's single resolution actor), resolution/navigation always follows
 * backend state, and the accepted transition remains IMMEDIATE (the hook
 * clears the payload; no ordinary-round exit handshake here).
 *
 * Outcome derivation is strictly from production payload fields
 * (sessionWinnerTeam, myTeam) — never from CSS classes or mock state.
 */

/** Localized team labels, resolved at call time (render re-runs on switch). */
const teamLabel = (team: number): string =>
  String(translate(team === 0 ? 'gameplay:team.a' : 'gameplay:team.b'));
const TEAM_COLOR = [DT.color.teamRed, DT.color.teamBlue] as const;
// Reference palette (Match End mock CSS) mapped where no token exists.
const PANEL_SURFACE  = DS2.color.surface;   // #1F1F1F — content surface (explicitly permitted; never the backdrop tint)
const PANEL_BORDER   = '#F6D98A';           // subtle light-gold panel border from the reference
const GOLD_SOLID     = '#E0B34E';           // trophy/crown gold + gradient-text fallback
const DEFEAT_CORAL   = '#FF9494';           // Defeat headline
const MUTED_WARM     = DT.color.textSecondary;

interface Props {
  /** sessionOver payload with a rematch snapshot (GameScreen routing invariant). */
  payload:       RoundEndPayload;
  /** Final-match rematch vote (= useRoundEndOverlay.submitRematchVote):
   *  single-flight, funnels the authoritative snapshot back through GameScene.
   *  Navigation always follows backend resolution, never local button state. */
  onRematchVote: (sessionId: number, vote: 'yes' | 'no') => Promise<void>;
}

type Outcome = 'victory' | 'defeat' | 'neutral';

/** Format an authoritative duration: MM:SS under an hour, H:MM:SS above. */
export function formatMatchLength(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const ss = String(s).padStart(2, '0');
  return h >= 1 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Inline trophy (Victory only) — decorative; the outcome heading carries the
 *  accessible text. Shape mirrors the reference's six vector blocks: cup,
 *  two handles, stem, two base bars. */
function TrophyIcon() {
  return (
    <svg viewBox="0 0 100 100" width="72" height="72" aria-hidden="true" focusable="false">
      <path d="M30 18 h40 v20 a20 20 0 0 1 -40 0 z" fill={GOLD_SOLID} />
      <path d="M30 23 h-11 a3 3 0 0 0 -3 3 v3 a15 15 0 0 0 15 15" stroke={GOLD_SOLID} strokeWidth="4.2" fill="none" />
      <path d="M70 23 h11 a3 3 0 0 1 3 3 v3 a15 15 0 0 1 -15 15" stroke={GOLD_SOLID} strokeWidth="4.2" fill="none" />
      <rect x="44" y="56" width="12" height="15" fill={GOLD_SOLID} />
      <rect x="34" y="71" width="32" height="8" fill={GOLD_SOLID} />
      <rect x="27" y="79" width="46" height="7" fill={GOLD_SOLID} />
    </svg>
  );
}

/** Decorative crown over the winning team's avatars (replaces the mock's
 *  emoji — no emojis in production UI). */
function CrownIcon() {
  return (
    <svg viewBox="0 0 24 16" width="18" height="12" aria-hidden="true" focusable="false">
      <path d="M2 14 L2 5 L7 9 L12 2 L17 9 L22 5 L22 14 Z" fill={GOLD_SOLID} />
    </svg>
  );
}

/** One team's column in the Teams strip: overlapping real avatars (crowned
 *  when this team won), human flags, TEAM label with (You) for the viewer's
 *  team, and the final score. */
function TeamColumn({
  team, players, score, isWinner, isMine, outcome,
}: {
  team: 0 | 1; players: Player[]; score: number;
  isWinner: boolean; isMine: boolean; outcome: Outcome;
}) {
  const ring = TEAM_COLOR[team];
  // Winner emphasis: gold score on Victory-viewer's own win; bright ivory for
  // the opposing winner on Defeat; muted for the losing side. Never色 alone —
  // the crown + accessible labels carry the meaning too.
  const scoreColor = isWinner
    ? (outcome === 'victory' && isMine ? PANEL_BORDER : DT.color.textPrimary)
    : MUTED_WARM;
  const names = players.map(p => getPlayerName(p, `P${p.position}`)).join(' & ');

  return (
    <div
      role="group"
      aria-label={String(translate('gameplay:matchEnd.teamAria', {
        team: teamLabel(team),
        mine: isMine ? translate('gameplay:matchEnd.yourTeamAria') : '',
        winners: isWinner ? translate('gameplay:matchEnd.winnersAria') : '',
        names, score,
      }))}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
    >
      {/* Overlapping avatar pair; the crown sits above the pair when this
          team won. Fixed slot height keeps both columns baseline-aligned
          whether or not a crown renders. */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', height: 58, paddingTop: 14 }}>
        {isWinner && (
          <span style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }} aria-hidden="true">
            <CrownIcon />
          </span>
        )}
        {players.map((p, i) => (
          <span
            key={p.position}
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              borderRadius: '50%',
              border: `2.5px solid ${ring}`,
              background: '#3C342B',
              marginLeft: i === 0 ? 0 : -10,
              zIndex: players.length - i,
            }}
          >
            <PlayerAvatar avatarKey={p.avatar_key} name={getPlayerName(p, `P${p.position}`)} size={40} loading="eager"
              initialStyle={{ background: 'transparent', border: 'none', fontSize: 15, fontWeight: 700, fontFamily: DT.font.sans, color: '#F6F2EA' }} />
          </span>
        ))}
      </div>

      {/* Human country flags (bots have none); compact row under the pair. */}
      <div style={{ display: 'flex', gap: 4, minHeight: 12, alignItems: 'center' }}>
        {players.filter(p => !p.is_bot && p.country_code).map(p => (
          <CountryFlag key={p.position} countryCode={p.country_code} width={16} loading="eager" />
        ))}
      </div>

      <div style={{
        fontSize: 12, fontWeight: 700, fontFamily: DT.font.sans, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: MUTED_WARM, whiteSpace: 'nowrap',
      }}>
        {teamLabel(team)}{isMine ? translate('gameplay:matchEnd.youSuffix') : ''}
      </div>

      <div style={{ fontSize: 30, fontWeight: 400, fontFamily: DT.font.heading, lineHeight: 1.1, color: scoreColor }}>
        {score}
      </div>
    </div>
  );
}

const MATCH_STYLES = `
.meo-center {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}
.meo-panel {
  /* Reference proportions (820px at the 2560 frame ≈ 32vw), responsive —
     never the mock's fixed absolute box. Height is content-driven; the
     max-height + internal scroll is a short/mobile-viewport fallback only. */
  width: clamp(480px, 32vw, 820px);
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 36px 32px 32px;
  border-radius: 21px;
  background: ${PANEL_SURFACE};
  border: 1px solid ${PANEL_BORDER};
  box-shadow: ${DT.shadow.gold};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
}
@media (max-width: 640px), (max-height: 900px) {
  .meo-center { padding: 16px; }
  .meo-panel  { padding: 22px 18px 20px; gap: 14px; }
}
@media (max-width: 640px) {
  .meo-panel { width: 100%; }
}

/* Outcome headline. Victory uses the reference's gold gradient text with a
   solid-gold fallback — without background-clip support, transparent fill
   would render invisible text, so the solid color is the default. */
.meo-headline {
  font-family: ${DT.font.heading};
  font-weight: 400;
  font-size: 44px;
  line-height: 1.1;
  text-align: center;
  outline: none;
}
@media (max-width: 640px), (max-height: 900px) {
  .meo-headline { font-size: 34px; }
}
.meo-headline--victory { color: ${GOLD_SOLID}; }
@supports ((-webkit-background-clip: text) or (background-clip: text)) {
  .meo-headline--victory {
    background: linear-gradient(90deg, #F6D98A 0%, ${GOLD_SOLID} 50%, #8A6A24 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
}
.meo-headline--defeat  { color: ${DEFEAT_CORAL}; }
.meo-headline--neutral { color: ${DT.color.textPrimary}; }

/* Stat cards (reference: subtle glass cards). */
.meo-stats {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
  width: 100%;
}
.meo-stat {
  flex: 1 1 0;
  max-width: 220px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
}
@media (max-width: 640px) {
  .meo-stats { flex-direction: column; align-items: stretch; }
  .meo-stat  { max-width: none; }
}

/* CTA row: two equal buttons on desktop, stacked full-width on mobile. */
.meo-cta {
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
}
.meo-cta > button { flex: 1 1 0; }
@media (max-width: 640px) {
  .meo-cta { flex-direction: column; }
}
`;

export function MatchEndOverlay({ payload, onRematchVote }: Props) {
  const { t } = useTranslation('gameplay');
  const {
    sessionId, targetScore, teamScores, players,
    sessionWinnerTeam, myTeam, myUserId, gameMode, rematch,
  } = payload;

  const headingId = useId();
  const sentenceId = useId();
  const headingRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Outcome — strictly from production payload fields ────────────────────
  const validTeam = myTeam === 0 || myTeam === 1;
  const outcome: Outcome =
    sessionWinnerTeam !== null && validTeam
      ? (myTeam === sessionWinnerTeam ? 'victory' : 'defeat')
      : 'neutral';

  const scoreA = teamScores['0'] ?? 0;
  const scoreB = teamScores['1'] ?? 0;
  const winner = sessionWinnerTeam;
  const loser  = winner === 0 ? 1 : 0;
  const winScore  = winner !== null ? (teamScores[String(winner)] ?? 0) : 0;
  const loseScore = winner !== null ? (teamScores[String(loser)] ?? 0) : 0;

  const headline =
    outcome === 'victory' ? t('matchEnd.victory') : outcome === 'defeat' ? t('matchEnd.defeat') : t('matchEnd.matchComplete');
  const sentence =
    outcome === 'victory' && winner !== null
      ? t('matchEnd.victorySentence', { winner: teamLabel(winner), loser: teamLabel(loser), w: winScore, l: loseScore })
      : outcome === 'defeat' && winner !== null
        ? t('matchEnd.defeatSentence', { winner: teamLabel(winner), w: winScore, l: loseScore })
        : t('matchEnd.neutralSentence', { a: scoreA, b: scoreB });

  // ── Teams strip data: real players grouped by authoritative team, seat order
  const teamPlayers = useMemo(() => {
    const sorted = [...players].sort((a, b) => a.position - b.position);
    return [sorted.filter(p => p.team === 0), sorted.filter(p => p.team === 1)] as const;
  }, [players]);

  // ── Real statistics (component-local, read-only, silently omitted) ────────
  const [matchLength, setMatchLength] = useState<string | null>(null);
  const [roundsWon, setRoundsWon] = useState<{ a: number; b: number } | null>(null);

  useEffect(() => {
    // Match length: exact-session entry from the existing history endpoint,
    // matched by session_id with the correct match type. Absent/null/error →
    // the card is omitted; never estimated from client time.
    let stale = false;
    apiClient.getMatchHistory(gameMode === 'bots' ? 'bots' : 'online_ranked')
      .then(res => {
        if (stale) return;
        const entry = res.history.find(e => e.session_id === sessionId);
        const ms = entry?.duration_ms;
        if (typeof ms === 'number' && ms > 0) setMatchLength(formatMatchLength(ms));
      })
      .catch(() => { /* omit the card — no retry loop, no error surface */ });
    return () => { stale = true; };
  }, [sessionId, gameMode]);

  useEffect(() => {
    // Rounds won: authoritative per-round winner_team from the participant
    // session-games endpoint. Ties (winner_team null) and non-finished games
    // count for neither team. Error/empty → card omitted.
    let stale = false;
    apiClient.getSessionGames(sessionId)
      .then(res => {
        if (stale) return;
        const finished = (res.games ?? []).filter(g => g.status === 'finished');
        if (finished.length === 0) return;
        const a = finished.filter(g => g.winner_team === 0).length;
        const b = finished.filter(g => g.winner_team === 1).length;
        setRoundsWon({ a, b });
      })
      .catch(() => { /* omit the card */ });
    return () => { stale = true; };
  }, [sessionId]);

  // ── Rematch vote state (authoritative snapshot + local request tracking) ──
  const myRecordedVote: 'yes' | 'no' | null =
    (rematch && myUserId ? rematch.votes[String(myUserId)] : null) ?? null;
  const recordedRef = useRef(myRecordedVote);
  recordedRef.current = myRecordedVote;

  const [voteChoice, setVoteChoice] = useState<'yes' | 'no' | null>(null);
  const [voteStatus, setVoteStatus] = useState<'idle' | 'pending' | 'failed'>('idle');
  const voteStatusRef = useRef(voteStatus);
  voteStatusRef.current = voteStatus;

  const castVote = useCallback(async (vote: 'yes' | 'no') => {
    if (recordedRef.current || voteStatusRef.current === 'pending') return;
    setVoteChoice(vote);
    setVoteStatus('pending');
    await onRematchVote(sessionId, vote);
    // Success is proven ONLY by the vote appearing in the authoritative
    // snapshot (the hook funnels the response through GameScene back into
    // payload.rematch.votes synchronously via window events). One timer tick
    // lets that re-render land; a still-absent vote means the request failed
    // — re-enable for retry, never show an optimistic checkmark.
    window.setTimeout(() => {
      if (recordedRef.current) {
        setVoteStatus('idle');
      } else {
        setVoteStatus('failed');
        setVoteChoice(null);
      }
    }, 0);
  }, [onRematchVote, sessionId]);

  const effectiveVote: 'yes' | 'no' | null =
    myRecordedVote ?? (voteStatus === 'pending' ? voteChoice : null);
  const votePending = voteStatus === 'pending' && myRecordedVote === null;

  // ── Rematch countdown — DISPLAY-ONLY from the persisted server deadline.
  //    Reaching zero triggers nothing locally; resolution is always the
  //    authoritative backend broadcast (RematchDecisionJob). ────────────────
  const deadlineMs = useMemo(
    () => (rematch?.deadline_at ? Date.parse(rematch.deadline_at) : null),
    [rematch?.deadline_at],
  );
  const remaining = useCallback(
    () => (deadlineMs == null ? null : Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000))),
    [deadlineMs],
  );
  const [countdown, setCountdown] = useState<number | null>(remaining);
  useEffect(() => {
    if (deadlineMs == null || rematch?.state !== 'pending') { setCountdown(null); return; }
    setCountdown(remaining());
    const t = setInterval(() => setCountdown(remaining()), 250);
    return () => clearInterval(t);
  }, [deadlineMs, rematch?.state, remaining]);

  // Mode-owned decline copy — authoritative payload.gameMode, never the
  // rendered player list. Matches the real destinations in rematchResolution.ts:
  // bots → Home (no route auto-starts a game), online → Play Online (the
  // real non-auto-start table/lobby screen).
  const declineLabel = gameMode === 'bots' ? t('matchEnd.backHome') : t('matchEnd.backToLobby');
  const rejectionDestination = gameMode === 'bots' ? t('matchEnd.destHome') : t('matchEnd.destPlayOnline');

  // Single persistent polite status region: its text changes only on genuine
  // STATE transitions (accepted / rejected / vote failure) — never per
  // countdown tick, and the node itself is never recreated.
  const statusMessage =
    rematch?.state === 'accepted' ? t('matchEnd.accepted')
    : rematch?.state === 'rejected' ? t('matchEnd.declined', { dest: rejectionDestination })
    : voteStatus === 'failed' ? t('matchEnd.voteError')
    : '';

  const resolved = rematch?.state === 'accepted' || rematch?.state === 'rejected';

  return (
    <GameplayBackdrop
      active
      initialFocusRef={headingRef}
      panelRef={panelRef}
      ariaLabelledBy={headingId}
      ariaDescribedBy={sentenceId}
    >
      <style>{MATCH_STYLES}</style>
      <div className="meo-center">
        <div className="meo-panel">

          {outcome === 'victory' && <TrophyIcon />}

          {/* Micro-label — dynamic target, never a fixed round total. */}
          <div style={{
            textAlign: 'center', fontSize: 11, fontWeight: 400, fontFamily: DT.font.sans,
            letterSpacing: '0.35em', textTransform: 'uppercase', color: DT.color.gold,
          }}>
            {t('matchEnd.microLabel', { target: targetScore })}
          </div>

          {/* Outcome heading — also the initial focus target so the panel
              opens at the top (tabIndex -1: programmatically focusable,
              outside the Tab order). */}
          <div data-tutorial-target="matchend-outcome" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              id={headingId}
              ref={headingRef}
              tabIndex={-1}
              className={`meo-headline meo-headline--${outcome}`}
            >
              {headline}
            </div>
            <div id={sentenceId} style={{
              fontSize: 15, fontWeight: 600, fontFamily: DT.font.sans, color: MUTED_WARM, textAlign: 'center',
            }}>
              {sentence}
            </div>
          </div>

          {/* Teams strip: Team A vs Team B with real identities. */}
          <div data-tutorial-target="matchend-teams" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 22 }}>
            <TeamColumn
              team={0} players={teamPlayers[0]} score={scoreA}
              isWinner={winner === 0} isMine={myTeam === 0} outcome={outcome}
            />
            <div aria-hidden="true" style={{
              fontSize: 15, fontWeight: 700, fontFamily: DT.font.heading, color: 'rgba(168,159,134,0.75)',
            }}>
              VS
            </div>
            <TeamColumn
              team={1} players={teamPlayers[1]} score={scoreB}
              isWinner={winner === 1} isMine={myTeam === 1} outcome={outcome}
            />
          </div>

          {/* Real statistics only — rendered solely when authoritative data
              arrived. No rating (no rating system exists), no MVP/best-round
              (not exposed by any endpoint). */}
          {(matchLength !== null || roundsWon !== null) && (
            <div className="meo-stats">
              {matchLength !== null && (
                <div className="meo-stat">
                  <div style={{ fontSize: 20, fontFamily: DT.font.heading, color: '#F6F2EA' }}>{matchLength}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, fontFamily: DT.font.sans, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(168,159,134,0.75)' }}>
                    {t('matchEnd.matchLength')}
                  </div>
                </div>
              )}
              {roundsWon !== null && (
                <div className="meo-stat">
                  <div style={{ fontSize: 20, fontFamily: DT.font.heading, color: '#F6F2EA' }}>{`${roundsWon.a}–${roundsWon.b}`}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, fontFamily: DT.font.sans, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(168,159,134,0.75)' }}>
                    {t('matchEnd.roundsWon')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rematch actions — hidden once the vote resolved (the status line
              below reports the resolution; navigation is authoritative). */}
          {rematch && !resolved && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
              <div className="meo-cta" data-tutorial-target="matchend-actions">
                <button
                  type="button"
                  onClick={() => void castVote('no')}
                  disabled={effectiveVote !== null}
                  aria-label={votePending && voteChoice === 'no' ? t('matchEnd.submittingDecline') : undefined}
                  style={{
                    minHeight: 48, padding: '10px 16px', border: 'none',
                    borderRadius: DS2.modal.buttonRadius,
                    background: DS2.modal.action.red.background,
                    color: DS2.modal.action.red.text,
                    fontSize: 15, fontWeight: 700, fontFamily: DT.font.sans,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    cursor: effectiveVote !== null ? 'default' : 'pointer',
                    opacity: effectiveVote !== null && effectiveVote !== 'no' ? 0.5 : 1,
                  }}
                >
                  {votePending && voteChoice === 'no' ? (
                    <span aria-hidden="true" style={{
                      width: 16, height: 16, borderRadius: '50%',
                      border: '2px solid rgba(246,242,234,0.35)', borderTopColor: DS2.modal.action.red.text,
                      display: 'inline-block', animation: 'spin 0.75s linear infinite',
                    }} />
                  ) : effectiveVote === 'no' ? t('matchEnd.leaving') : declineLabel}
                </button>
                <button
                  type="button"
                  onClick={() => void castVote('yes')}
                  disabled={effectiveVote !== null}
                  aria-label={votePending && voteChoice === 'yes' ? t('matchEnd.submittingVote') : undefined}
                  style={{
                    minHeight: 48, padding: '10px 16px',
                    borderRadius: DS2.modal.buttonRadius,
                    background: DS2.modal.action.gold.background,
                    border: DS2.modal.action.gold.border,
                    color: DS2.modal.action.gold.text,
                    fontSize: 15, fontWeight: 700, fontFamily: DT.font.sans,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    cursor: effectiveVote !== null ? 'default' : 'pointer',
                    opacity: effectiveVote !== null && effectiveVote !== 'yes' ? 0.5 : 1,
                  }}
                >
                  {votePending && voteChoice === 'yes' ? (
                    <span aria-hidden="true" style={{
                      width: 16, height: 16, borderRadius: '50%',
                      border: '2px solid rgba(36,26,9,0.35)', borderTopColor: DS2.modal.action.gold.text,
                      display: 'inline-block', animation: 'spin 0.75s linear infinite',
                    }} />
                  ) : myRecordedVote === 'yes' ? t('matchEnd.acceptedBtn') : t('matchEnd.rematch')}
                </button>
              </div>

              {/* Truthful consequence copy + authoritative vote progress. */}
              <div style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: MUTED_WARM }}>
                {t('matchEnd.allMustAccept')}
              </div>
              <div style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: MUTED_WARM }}>
                {t('matchEnd.voteCount', { yes: rematch.yes_count, req: rematch.required_human_count })}
                {/* Ticking countdown: VISUAL ONLY — deliberately outside any
                    live region so it never announces per tick. Zero triggers
                    nothing; the backend job resolves the vote. */}
                {countdown != null && (
                  <> {t('matchEnd.returningIn', { dest: rejectionDestination, n: countdown })}</>
                )}
              </div>
            </div>
          )}

          {/* ONE persistent polite status region — announces state changes
              (accepted / declined / vote failure) exactly once each; empty
              text otherwise. Never contains the ticking countdown. */}
          <div role="status" style={{
            textAlign: 'center', fontSize: 12, fontFamily: DT.font.sans,
            color: rematch?.state === 'accepted' ? DT.color.gold
              : voteStatus === 'failed' ? DT.color.error : MUTED_WARM,
            minHeight: statusMessage ? undefined : 0,
          }}>
            {statusMessage}
          </div>

        </div>
      </div>
    </GameplayBackdrop>
  );
}
